import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { onIdTokenChanged, signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDocs,
  getDoc,
  documentId,
  setDoc,
} from "firebase/firestore";
import { isOffline } from "../utils/offline";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [ownedProfiles, setOwnedProfiles] = useState([]);
  const [sharedProfiles, setSharedProfiles] = useState([]);
  const [activeCareProfile, setActiveCareProfile] = useState(null);
  const [isProfessional, setIsProfessional] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [profilesUnavailableOffline, setProfilesUnavailableOffline] =
    useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [defaultProfileId, setDefaultProfileId] = useState(null);

  const selectActiveCareProfile = (profile) => {
    setActiveCareProfile(profile);
    if (profile) {
      localStorage.setItem("activeCareProfileId", profile.id);
    } else {
      localStorage.removeItem("activeCareProfileId");
    }
  };

  const fetchUserAndProfiles = useCallback(async (user) => {
    if (!user) {
      setOwnedProfiles([]);
      setSharedProfiles([]);
      setActiveCareProfile(null);
      setDefaultProfileId(null);
      setProfilesUnavailableOffline(false);
      setProfilesLoading(false);
      return;
    }

    setProfilesLoading(true);
    try {
      const userDocRef = doc(db, "users", user.uid);
      const ownedQuery = query(
        collection(db, "careProfiles"),
        where("userId", "==", user.uid),
      );
      const accessibleProfilesColRef = collection(
        userDocRef,
        "accessibleProfiles",
      );

      const [userDocSnap, ownedSnapshot, accessibleProfilesSnapshot] =
        await Promise.all([
          getDoc(userDocRef),
          getDocs(ownedQuery),
          getDocs(accessibleProfilesColRef),
        ]);

      const newDefaultId = userDocSnap.exists()
        ? userDocSnap.data().defaultProfileId
        : null;
      const owned = ownedSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      let shared = [];
      const accessibleIds = accessibleProfilesSnapshot.docs.map((d) => d.id);
      let snapshotsFromCache =
        userDocSnap.metadata?.fromCache ||
        ownedSnapshot.metadata?.fromCache ||
        accessibleProfilesSnapshot.metadata?.fromCache;

      if (
        isOffline() &&
        snapshotsFromCache &&
        owned.length === 0 &&
        accessibleIds.length === 0
      ) {
        setProfilesUnavailableOffline(true);
        return;
      }

      // Create a map of profileId to access metadata for easy lookup
      const accessibleProfileMeta = accessibleProfilesSnapshot.docs.reduce(
        (acc, doc) => {
          acc[doc.id] = doc.data();
          return acc;
        },
        {},
      );

      if (accessibleIds.length > 0) {
        const sharedQuery = query(
          collection(db, "careProfiles"),
          where(documentId(), "in", accessibleIds),
        );
        const sharedSnapshot = await getDocs(sharedQuery);
        snapshotsFromCache =
          snapshotsFromCache || sharedSnapshot.metadata?.fromCache;
        // When mapping over shared profiles, add the role from our map
        shared = sharedSnapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
            relationship: accessibleProfileMeta[d.id]?.relationship,
            role: accessibleProfileMeta[d.id]?.role,
          }))
          // Exclude profiles the current user actually owns, in case a legacy self-reference exists
          .filter((p) => p.userId !== user.uid);
      }

      if (
        isOffline() &&
        snapshotsFromCache &&
        owned.length + shared.length === 0
      ) {
        setProfilesUnavailableOffline(true);
        return;
      }

      setProfilesUnavailableOffline(false);
      setOwnedProfiles(owned);
      setSharedProfiles(shared);
      setDefaultProfileId(newDefaultId);

      const allProfiles = [...owned, ...shared];
      let profileToSelect = null;
      const lastActiveProfileId = localStorage.getItem("activeCareProfileId");

      if (newDefaultId) {
        profileToSelect = allProfiles.find((p) => p.id === newDefaultId);
      }
      if (!profileToSelect && lastActiveProfileId) {
        profileToSelect = allProfiles.find((p) => p.id === lastActiveProfileId);
      }
      if (!profileToSelect && user.displayName) {
        profileToSelect = allProfiles.find(
          (p) => p.profileName === user.displayName,
        );
      }
      if (!profileToSelect && allProfiles.length > 0) {
        profileToSelect = allProfiles[0];
      }

      selectActiveCareProfile(profileToSelect);

      // Build roles mapping based on accessibleProfiles subcollection
      const rolesMap = {};
      accessibleProfilesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.role) {
          rolesMap[docSnap.id] = data.role;
        }
      });
      if (Object.keys(rolesMap).length > 0) {
        setUserRole((prev) => ({ ...(prev || {}), ...rolesMap }));
      }
    } catch (error) {
      console.error("Error fetching user and profile data:", error);
      if (isOffline() || error?.code === "unavailable") {
        setProfilesUnavailableOffline(true);
      }
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  useEffect(() => {
    let unsubscribeInvites = null;
    let unsubscribeAccessible = null;

    const cleanupProfileListeners = () => {
      if (unsubscribeInvites) {
        unsubscribeInvites();
        unsubscribeInvites = null;
      }
      if (unsubscribeAccessible) {
        unsubscribeAccessible();
        unsubscribeAccessible = null;
      }
    };

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      cleanupProfileListeners();
      setCurrentUser(user);
      setLoading(true);
      if (user) {
        try {
          await fetchUserAndProfiles(user);

          try {
            const idTokenResult = await user.getIdTokenResult();
            setUserRole((prev) => ({
              ...(prev || {}),
              ...(idTokenResult.claims.roles || {}),
            }));
            setIsProfessional(idTokenResult.claims.isProfessional || false);
          } catch (error) {
            console.warn("Unable to refresh token claims:", error);
          }

          const signInTriggered = sessionStorage.getItem("signInTriggered");
          if (!signInTriggered && !isOffline()) {
            addDoc(collection(db, "user_sign_ins"), {
              uid: user.uid,
              email: user.email,
              signInTime: Timestamp.now(),
            }).catch((error) => {
              console.warn("Unable to record sign-in event:", error);
            });
            sessionStorage.setItem("signInTriggered", "true");
          }

          const invitationsQuery = query(
            collection(db, "invitations"),
            where("inviteeEmail", "==", user.email),
            where("status", "==", "pending"),
          );
          unsubscribeInvites = onSnapshot(
            invitationsQuery,
            (snapshot) => {
              setPendingInvitations(
                snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
              );
            },
            (error) => {
              console.warn("Unable to listen for invitations:", error);
              setPendingInvitations([]);
            },
          );

          const accessibleProfilesRef = collection(
            db,
            "users",
            user.uid,
            "accessibleProfiles",
          );
          let isFirstRun = true;
          unsubscribeAccessible = onSnapshot(
            accessibleProfilesRef,
            () => {
              if (isFirstRun) {
                isFirstRun = false;
                return;
              }
              fetchUserAndProfiles(user);
            },
            (error) => {
              console.warn(
                "Unable to listen for profile access changes:",
                error,
              );
            },
          );

          // Force an update check only when online; offline startup should not wait on it.
          if (!isOffline() && "serviceWorker" in navigator) {
            navigator.serviceWorker
              .getRegistration()
              .then((reg) => {
                if (reg) {
                  reg.update();
                }
              })
              .catch((error) => {
                console.warn(
                  "Unable to check for service worker updates:",
                  error,
                );
              });
          }
        } catch (error) {
          console.error("Error during authenticated startup:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setUserRole(null);
        setOwnedProfiles([]);
        setSharedProfiles([]);
        setActiveCareProfile(null);
        setPendingInvitations([]);
        setDefaultProfileId(null);
        setIsProfessional(false);
        setProfilesUnavailableOffline(false);
        sessionStorage.removeItem("signInTriggered");
        setProfilesLoading(false);
        setLoading(false);
      }
    });
    return () => {
      cleanupProfileListeners();
      unsubscribe();
    };
  }, [fetchUserAndProfiles]);

  useEffect(() => {
    if (
      !currentUser ||
      !profilesUnavailableOffline ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const retryProfileLoad = () => {
      fetchUserAndProfiles(currentUser);
    };

    window.addEventListener("online", retryProfileLoad);
    if (!isOffline()) {
      retryProfileLoad();
    }

    return () => window.removeEventListener("online", retryProfileLoad);
  }, [currentUser, fetchUserAndProfiles, profilesUnavailableOffline]);

  const addCareProfile = async (profileData) => {
    if (!currentUser) throw new Error("User not authenticated");

    const isFirstProfile =
      ownedProfiles.length === 0 && sharedProfiles.length === 0;

    const serverTimestamp = Timestamp.now();
    const docRef = await addDoc(collection(db, "careProfiles"), {
      ...profileData,
      userId: currentUser.uid,
      accountEmail: currentUser.email?.toLowerCase() || "",
      memberUIDs: [currentUser.uid],
      createdAt: serverTimestamp,
      updatedAt: serverTimestamp,
    });

    // Immediately reflect in local state instead of waiting for re-fetch
    const newProfile = {
      id: docRef.id,
      ...profileData,
      userId: currentUser.uid,
      accountEmail: currentUser.email?.toLowerCase() || "",
      memberUIDs: [currentUser.uid],
      createdAt: serverTimestamp,
      updatedAt: serverTimestamp,
    };
    setOwnedProfiles((prev) => [...prev, newProfile]);
    selectActiveCareProfile(newProfile);

    if (isFirstProfile) {
      await setDoc(
        doc(db, "users", currentUser.uid),
        { defaultProfileId: docRef.id },
        { merge: true },
      );
      setDefaultProfileId(docRef.id);
    }

    return docRef;
  };

  const updateCareProfile = async (profileId, profileData) => {
    if (!currentUser) throw new Error("User not authenticated");
    const profileDocRef = doc(db, "careProfiles", profileId);
    await updateDoc(profileDocRef, {
      ...profileData,
      updatedAt: Timestamp.now(),
    });
    await fetchUserAndProfiles(currentUser);
  };

  const deleteCareProfile = async (profileId) => {
    if (!currentUser) throw new Error("User not authenticated");
    await deleteDoc(doc(db, "careProfiles", profileId));
    await fetchUserAndProfiles(currentUser);
  };

  const acceptInvitation = async (invitation) => {
    if (!currentUser) throw new Error("No user is currently signed in.");
    const { id: invitationId, careProfileId } = invitation;
    const user = auth.currentUser;
    const API_BASE_URL = `https://asia-southeast1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;
    const apiEndpoint = `${API_BASE_URL}/care-team/accept-invitation`;
    const response = await fetch(apiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await user.getIdToken()}`,
      },
      body: JSON.stringify({ invitationId, careProfileId }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to accept invitation.");
    }
    await user.getIdToken(true);
    await fetchUserAndProfiles(user);
    return await response.json();
  };

  const declineInvitation = async (invitationId) => {
    if (!currentUser) throw new Error("User not authenticated");
    await updateDoc(doc(db, "invitations", invitationId), {
      status: "declined",
      respondedAt: Timestamp.now(),
    });
    setPendingInvitations((prev) =>
      prev.filter((inv) => inv.id !== invitationId),
    );
  };

  const leaveSharedProfile = async (careProfileId) => {
    // Optimistic UI update
    const profileToRemove = sharedProfiles.find((p) => p.id === careProfileId);
    setSharedProfiles((prev) => prev.filter((p) => p.id !== careProfileId));
    if (activeCareProfile?.id === careProfileId) {
      // If the active profile is the one being left, switch to the default or first available profile
      const newActiveProfile =
        ownedProfiles.find((p) => p.id === defaultProfileId) ||
        ownedProfiles[0] ||
        null;
      selectActiveCareProfile(newActiveProfile);
    }

    try {
      if (!currentUser) throw new Error("No user is currently signed in.");
      const user = auth.currentUser;
      const API_BASE_URL = `https://asia-southeast1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;
      const apiEndpoint = `${API_BASE_URL}/care-team/leave-care-team`;
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await user.getIdToken()}`,
        },
        body: JSON.stringify({ careProfileId }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to leave care team.");
      }
      // On success, we just need to ensure the user's token is refreshed. The UI is already updated.
      await user.getIdToken(true);
    } catch (error) {
      console.error("Error leaving care team:", error);
      // If the API call fails, revert the optimistic update
      if (profileToRemove) {
        setSharedProfiles((prev) => [...prev, profileToRemove]);
      }
      // Optionally, re-select the profile if it was the active one
      if (activeCareProfile?.id === careProfileId) {
        selectActiveCareProfile(profileToRemove);
      }
      // Re-throw the error to be caught by the UI component
      throw error;
    }
  };

  const setDefaultCareProfile = async (profileId) => {
    if (!currentUser) throw new Error("User not authenticated");
    const userDocRef = doc(db, "users", currentUser.uid);
    await setDoc(userDocRef, { defaultProfileId: profileId }, { merge: true });
    setDefaultProfileId(profileId);
    const newActiveProfile = [...ownedProfiles, ...sharedProfiles].find(
      (p) => p.id === profileId,
    );
    if (newActiveProfile) {
      selectActiveCareProfile(newActiveProfile);
    }
  };

  const value = {
    currentUser,
    userRole,
    isProfessional,
    logout: () => signOut(auth),
    loading,
    profilesLoading,
    profilesUnavailableOffline,
    ownedProfiles,
    sharedProfiles,
    activeCareProfile,
    selectActiveCareProfile,
    addCareProfile,
    updateCareProfile,
    deleteCareProfile,
    pendingInvitations,
    acceptInvitation,
    declineInvitation,
    leaveSharedProfile,
    defaultProfileId,
    setDefaultCareProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
